import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    Users, 
    CreditCard,
    Building2,
    Eye,
    EyeOff,
    RefreshCw,
    Shield,
    Key,
    Copy,
    Check,
    Edit,
    User,
    Clock,
    CheckCircle2,
    XCircle
} from 'lucide-react';
import { cn } from '../lib/utils';

// ============================================================
// IDENTIFIANTS SYSTÈME - ADMIN UNIQUEMENT
// Vue complète des utilisateurs, caisses et établissements
// ============================================================

// Role badge colors
const ROLE_COLORS = {
    ADMIN: 'bg-red-100 text-red-800 border-red-200',
    DIRECTEUR: 'bg-purple-100 text-purple-800 border-purple-200',
    DIRECTEUR_ADJOINT: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    MAITRE_HOTEL: 'bg-blue-100 text-blue-800 border-blue-200',
    SERVEUR: 'bg-green-100 text-green-800 border-green-200',
    SERVEUSE: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    BARMAN: 'bg-amber-100 text-amber-800 border-amber-200',
    CHEF_CUISINE: 'bg-orange-100 text-orange-800 border-orange-200',
};

const ROLE_LABELS = {
    ADMIN: 'Admin',
    DIRECTEUR: 'Directeur',
    DIRECTEUR_ADJOINT: 'Dir. Adjoint',
    MAITRE_HOTEL: "Maître d'Hôtel",
    SERVEUR: 'Serveur',
    SERVEUSE: 'Serveuse',
    BARMAN: 'Barman',
    CHEF_CUISINE: 'Chef Cuisine',
};

const SystemCredentialsPage = () => {
    const { api, user } = useAuth();
    
    // State - Must be declared before any conditional return
    const [users, setUsers] = useState([]);
    const [registers, setRegisters] = useState([]);
    const [establishments, setEstablishments] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showPins, setShowPins] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const [editPinDialog, setEditPinDialog] = useState({ open: false, user: null });
    const [newPin, setNewPin] = useState('');
    
    const isAdmin = user?.role === 'ADMIN';
    
    // Fetch data
    const fetchData = useCallback(async () => {
        if (!isAdmin) return;
        try {
            setLoading(true);
            const [usersRes, registersRes, estRes, summaryRes] = await Promise.all([
                api().get('/admin/credentials/users'),
                api().get('/admin/credentials/registers'),
                api().get('/admin/credentials/establishments'),
                api().get('/admin/credentials/summary')
            ]);
            
            setUsers(usersRes.data.users || []);
            setRegisters(registersRes.data.registers || []);
            setEstablishments(estRes.data.establishments || []);
            setSummary(summaryRes.data);
        } catch (error) {
            console.error('Error fetching credentials:', error);
            toast.error('Erreur lors du chargement des identifiants');
        } finally {
            setLoading(false);
        }
    }, [api, isAdmin]);
    
    useEffect(() => {
        if (isAdmin) {
            fetchData();
        }
    }, [fetchData, isAdmin]);
    
    // Copy to clipboard
    const copyToClipboard = async (text, id) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 2000);
            toast.success('Copié dans le presse-papier');
        } catch (err) {
            toast.error('Erreur de copie');
        }
    };
    
    // Update PIN
    const handleUpdatePin = async () => {
        if (!newPin || newPin.length < 4) {
            toast.error('Le PIN doit contenir au moins 4 chiffres');
            return;
        }
        
        try {
            await api().put(`/admin/credentials/users/${editPinDialog.user.id}/pin`, {
                pin: newPin
            });
            toast.success('PIN mis à jour');
            setEditPinDialog({ open: false, user: null });
            setNewPin('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        }
    };
    
    // Check if user is admin - AFTER all hooks
    if (!isAdmin) {
        return (
            <div className="flex items-center justify-center h-96">
                <Card className="p-8 text-center">
                    <Shield className="w-16 h-16 mx-auto text-red-500 mb-4" />
                    <h2 className="text-xl font-bold text-red-600">Accès Refusé</h2>
                    <p className="text-muted-foreground mt-2">
                        Cette page est réservée à l'administrateur.
                    </p>
                </Card>
            </div>
        );
    }
    
    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }
    
    return (
        <div className="space-y-4 p-4 md:p-6" data-testid="system-credentials-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                    <h1 className="text-xl font-bold flex items-center gap-2">
                        <Key className="w-6 h-6 text-primary" />
                        Identifiants Système
                    </h1>
                    <p className="text-xs text-muted-foreground">
                        Vue complète des accès utilisateurs et caisses • Admin uniquement
                    </p>
                </div>
                
                <div className="flex items-center gap-2">
                    <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setShowPins(!showPins)}
                        className="h-8"
                    >
                        {showPins ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                        {showPins ? 'Masquer PINs' : 'Afficher PINs'}
                    </Button>
                    <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={fetchData}
                        className="h-8"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </Button>
                </div>
            </div>
            
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-muted-foreground">Utilisateurs</p>
                                <p className="text-2xl font-bold">{summary?.users?.total || 0}</p>
                                <p className="text-xs text-green-600">{summary?.users?.active || 0} actifs</p>
                            </div>
                            <Users className="w-8 h-8 text-blue-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-muted-foreground">Caisses</p>
                                <p className="text-2xl font-bold">{summary?.registers?.total || 0}</p>
                                <p className="text-xs text-green-600">{summary?.registers?.active || 0} actives</p>
                            </div>
                            <CreditCard className="w-8 h-8 text-green-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-muted-foreground">Sessions Ouvertes</p>
                                <p className="text-2xl font-bold text-amber-600">{summary?.registers?.sessions_open || 0}</p>
                                <p className="text-xs text-muted-foreground">en cours</p>
                            </div>
                            <Clock className="w-8 h-8 text-amber-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-muted-foreground">Établissements</p>
                                <p className="text-2xl font-bold">{establishments.length}</p>
                            </div>
                            <Building2 className="w-8 h-8 text-purple-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
            </div>
            
            {/* Tabs */}
            <Tabs defaultValue="users">
                <TabsList className="h-9">
                    <TabsTrigger value="users" className="text-xs">
                        <Users className="w-3.5 h-3.5 mr-1" />
                        Utilisateurs ({users.length})
                    </TabsTrigger>
                    <TabsTrigger value="registers" className="text-xs">
                        <CreditCard className="w-3.5 h-3.5 mr-1" />
                        Caisses ({registers.length})
                    </TabsTrigger>
                    <TabsTrigger value="establishments" className="text-xs">
                        <Building2 className="w-3.5 h-3.5 mr-1" />
                        Établissements
                    </TabsTrigger>
                </TabsList>
                
                {/* Users Tab */}
                <TabsContent value="users" className="mt-4">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Liste des Utilisateurs</CardTitle>
                            <CardDescription className="text-xs">
                                Identifiants et codes PIN de tous les utilisateurs du système
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="border rounded-lg overflow-hidden">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/50">
                                            <TableHead className="text-xs">Nom</TableHead>
                                            <TableHead className="text-xs">Identifiant</TableHead>
                                            <TableHead className="text-xs">Rôle</TableHead>
                                            <TableHead className="text-xs">Code PIN</TableHead>
                                            <TableHead className="text-xs">Statut</TableHead>
                                            <TableHead className="text-xs text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {users.map(u => (
                                            <TableRow key={u.id}>
                                                <TableCell className="font-medium py-2">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                                                            <User className="w-4 h-4 text-muted-foreground" />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-medium">{u.name}</p>
                                                            {u.email && <p className="text-[10px] text-muted-foreground">{u.email}</p>}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="py-2">
                                                    <div className="flex items-center gap-1">
                                                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                                                            {u.username || u.email}
                                                        </code>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-6 w-6"
                                                            onClick={() => copyToClipboard(u.username || u.email, `user-${u.id}`)}
                                                        >
                                                            {copiedId === `user-${u.id}` ? (
                                                                <Check className="w-3 h-3 text-green-500" />
                                                            ) : (
                                                                <Copy className="w-3 h-3" />
                                                            )}
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="py-2">
                                                    <Badge 
                                                        variant="outline" 
                                                        className={cn("text-[10px]", ROLE_COLORS[u.role] || 'bg-gray-100')}
                                                    >
                                                        {ROLE_LABELS[u.role] || u.role}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="py-2">
                                                    <div className="flex items-center gap-1">
                                                        <code className={cn(
                                                            "text-sm font-mono px-2 py-0.5 rounded",
                                                            showPins ? "bg-amber-100 text-amber-800" : "bg-muted"
                                                        )}>
                                                            {showPins ? (u.pin || '—') : '••••••'}
                                                        </code>
                                                        {showPins && u.pin && (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-6 w-6"
                                                                onClick={() => copyToClipboard(u.pin, `pin-${u.id}`)}
                                                            >
                                                                {copiedId === `pin-${u.id}` ? (
                                                                    <Check className="w-3 h-3 text-green-500" />
                                                                ) : (
                                                                    <Copy className="w-3 h-3" />
                                                                )}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="py-2">
                                                    {u.is_active ? (
                                                        <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200">
                                                            <CheckCircle2 className="w-3 h-3 mr-1" />
                                                            Actif
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">
                                                            <XCircle className="w-3 h-3 mr-1" />
                                                            Inactif
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell className="py-2 text-right">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7"
                                                        onClick={() => setEditPinDialog({ open: true, user: u })}
                                                    >
                                                        <Edit className="w-3 h-3 mr-1" />
                                                        PIN
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
                
                {/* Registers Tab */}
                <TabsContent value="registers" className="mt-4">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Caisses Enregistreuses</CardTitle>
                            <CardDescription className="text-xs">
                                Liste des caisses avec utilisateurs assignés et sessions actives
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {registers.map(reg => (
                                    <Card key={reg.id} className={cn(
                                        "border-l-4",
                                        reg.has_active_session ? "border-l-green-500" : "border-l-gray-300"
                                    )}>
                                        <CardContent className="p-4">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <h3 className="font-semibold flex items-center gap-2">
                                                        <CreditCard className="w-4 h-4" />
                                                        {reg.name}
                                                    </h3>
                                                    <p className="text-xs text-muted-foreground">
                                                        Secteur: {reg.sector || 'Non défini'}
                                                    </p>
                                                </div>
                                                <Badge variant={reg.is_active ? "default" : "secondary"} className="text-[10px]">
                                                    {reg.is_active ? 'Active' : 'Inactive'}
                                                </Badge>
                                            </div>
                                            
                                            {/* Active Session */}
                                            {reg.has_active_session && (
                                                <div className="mt-3 p-2 bg-green-50 rounded-lg">
                                                    <p className="text-xs font-medium text-green-800">
                                                        Session en cours
                                                    </p>
                                                    <p className="text-[10px] text-green-700">
                                                        Ouverte par {reg.active_session?.opened_by} • 
                                                        Fond: {reg.active_session?.opening_cash?.toLocaleString()} F
                                                    </p>
                                                </div>
                                            )}
                                            
                                            {/* Assigned Users */}
                                            <div className="mt-3">
                                                <p className="text-xs font-medium mb-1">
                                                    Utilisateurs assignés ({reg.assigned_count})
                                                </p>
                                                {reg.assigned_users?.length > 0 ? (
                                                    <div className="flex flex-wrap gap-1">
                                                        {reg.assigned_users.map(u => (
                                                            <Badge key={u.id} variant="outline" className="text-[10px]">
                                                                {u.name}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-xs text-muted-foreground italic">
                                                        Aucun utilisateur assigné
                                                    </p>
                                                )}
                                            </div>
                                            
                                            {/* Printer */}
                                            {reg.printer_ip && (
                                                <p className="mt-2 text-[10px] text-muted-foreground">
                                                    Imprimante: {reg.printer_ip}
                                                </p>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
                
                {/* Establishments Tab */}
                <TabsContent value="establishments" className="mt-4">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Établissements</CardTitle>
                            <CardDescription className="text-xs">
                                Liste des établissements du système
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {establishments.map(est => (
                                    <Card key={est.id}>
                                        <CardContent className="p-4">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <h3 className="font-semibold flex items-center gap-2">
                                                        <Building2 className="w-4 h-4" />
                                                        {est.name}
                                                    </h3>
                                                    {est.address && (
                                                        <p className="text-xs text-muted-foreground mt-1">
                                                            {est.address}
                                                        </p>
                                                    )}
                                                </div>
                                                <Badge variant={est.is_active ? "default" : "secondary"} className="text-[10px]">
                                                    {est.is_active ? 'Actif' : 'Inactif'}
                                                </Badge>
                                            </div>
                                            
                                            <div className="mt-3 flex items-center gap-4 text-xs">
                                                <div className="flex items-center gap-1">
                                                    <Users className="w-3 h-3 text-muted-foreground" />
                                                    <span>{est.user_count} utilisateurs</span>
                                                </div>
                                                {est.phone && (
                                                    <div className="text-muted-foreground">
                                                        {est.phone}
                                                    </div>
                                                )}
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
            
            {/* Edit PIN Dialog */}
            <Dialog open={editPinDialog.open} onOpenChange={(open) => !open && setEditPinDialog({ open: false, user: null })}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Key className="w-5 h-5" />
                            Modifier le PIN
                        </DialogTitle>
                        <DialogDescription>
                            Modifier le code PIN de {editPinDialog.user?.name}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-xs text-muted-foreground mb-2">
                            PIN actuel: <code className="bg-muted px-1 rounded">{editPinDialog.user?.pin || 'Non défini'}</code>
                        </p>
                        <Input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            placeholder="Nouveau PIN (min. 4 chiffres)"
                            value={newPin}
                            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                            maxLength={10}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditPinDialog({ open: false, user: null })}>
                            Annuler
                        </Button>
                        <Button onClick={handleUpdatePin}>
                            Enregistrer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SystemCredentialsPage;
